/* Unified Statistics — shared vocabulary, colours and formatting.

   Terms follow CONTEXT.md: Revenue is everything the accounts earned (every
   stream); the part Sold in chat (PPVs + tips) has a Seller — AI, Team or
   Unknown; the rest is "not sold in chat". Every money figure comes as a
   {gross, net} pair and the page shows one side at a time (MoneyMode), except the
   headline, which always shows both.

   Purple and neutral tones only (Page redesign: compact layout). */

import type { StatCreditedSeller, StatPair, StatSeller } from '../../../api/client';

export type MoneyMode = 'gross' | 'net';

export const pick = (pair: StatPair | undefined, mode: MoneyMode): number => (pair ? pair[mode] : 0);

/** Platform fee share the creator keeps — the ledgers store gross. */
export const NET_SHARE = 0.8;
export const ledgerMoney = (gross: number, mode: MoneyMode) => (mode === 'net' ? gross * NET_SHARE : gross);

export const fmt = (n: number) => Math.round(n || 0).toLocaleString('en-US');
export const usd = (n: number) => `$${fmt(n)}`;
export const usd2 = (n: number) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const pct = (n: number, digits = 1) => `${(Number.isFinite(n) ? n : 0).toFixed(digits)}%`;
export const ratio = (a: number, b: number) => (b ? a / b : 0);
export const share = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);

/** Seller colours (design: AI var(--om-accent), Team var(--om-accent-3), Unknown var(--om-border-strong)). */
export const SELLER_COLOR: Record<StatSeller, string> = {
  ai: 'var(--om-accent)',
  team: 'var(--om-accent-3)',
  unknown: 'var(--om-border-strong)',
  not_in_chat: 'var(--om-border)',
};
export const CREDITED: StatCreditedSeller[] = ['ai', 'team', 'unknown'];
export const SELLERS: StatSeller[] = ['ai', 'team', 'unknown', 'not_in_chat'];

/** Hatched fill: "no data" (Team without CRM access) and "not sold in chat". */
export const HATCH = 'repeating-linear-gradient(135deg,var(--om-border-strong) 0 2px,transparent 2px 5px)';

/** Revenue types in display order, each with its purple shade. */
export const REVENUE_TYPES: { id: string; color: string }[] = [
  { id: 'ppvs', color: 'var(--om-accent-4)' },
  { id: 'tips', color: 'var(--om-accent-2)' },
  { id: 'mass', color: 'var(--om-accent)' },
  { id: 'subscriptions', color: 'var(--om-accent-3)' },
  { id: 'posts', color: 'var(--om-accent-hover)' },
  { id: 'streams', color: 'var(--om-accent-deep)' },
  { id: 'stream_tips', color: 'var(--om-accent-deep)' },
  { id: 'referrals', color: 'var(--om-accent-deep)' },
  { id: 'other', color: 'var(--om-border-strong)' },
];
export const typeColor = (id: string) => REVENUE_TYPES.find((t) => t.id === id)?.color ?? 'var(--om-border-strong)';
export const typeOrder = (id: string) => {
  const i = REVENUE_TYPES.findIndex((t) => t.id === id);
  return i < 0 ? REVENUE_TYPES.length : i;
};

/* ── Kyiv calendar helpers (presets) ─────────────────────────────── */
export function kyivToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv' }).format(new Date());
}

export function shiftDays(iso: string, days: number): string {
  // Anchored at UTC midnight: pure calendar-day maths, no DST drift.
  const dt = new Date(`${iso}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export const daysBetween = (start: string, end: string) =>
  Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000) + 1;

/** "2026-10-05" → "05.10" (axis labels, range chip). */
export function shortDay(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}.${m}` : iso;
}

export const PRESETS = [1, 7, 30, 90] as const;

/** CSV download (operator-facing export, like the CRM dashboard's). */
export function downloadCsv(filename: string, rows: (string | number)[][]) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
